@use '~/theme/theme' as *;
@use '~/theme/typography';

.addonWrapper {
  display: flex;
  align-items: center;
  width: 100%;
}

.addon {
  position: relative;
  background: var(--addon-background);
  border: var(--addon-border);
  color: var(--addon-color);
  height: 100%;
}

.startAddon {
  border-right: none;
  border-radius: 8px 0 0 8px;
}

.endAddon {
  border-left: none;
  border-radius: 0 8px 8px 0;
  display: flex;
  align-items: center;
  padding-left: 8px;
}

.endAddonContainer {
  display: flex;
  align-items: center;
  gap: 6px;
  min-width: 0;
}

.media {
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
}

.selectedDisplayLabel {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  min-width: 0;
  max-width: 120px;
  display: inline-block;
}

.inputWrapper {
  position: relative;
  flex-grow: 1;
}

.icon,
.endIcon {
  position: absolute;
}

.clearIcon {
  position: absolute;
  right: 0;

  &:hover {
    background: none;
  }
}

.inputWrapper:has(.endIcon) .clearIcon {
  right: spacing(4);
}

.control {
  position: absolute;
}

/* stylelint-disable no-descending-specificity */
.inputField {
  --background-color: white;
  --border-color: #{color(grey-600)};
  --input-color: #{color(grey-800)};
  --addon-background: white;
  --addon-border: #{border(grey-600)};
  --addon-color: #{color(grey-600)};
  --placeholder-color: #{color(grey-600)};

  input {
    text-overflow: ellipsis;
  }

  input,
  textarea {
    font-family: 'Public Sans', sans-serif;
    border-radius: 8px;
    border: 1px solid var(--border-color);
    background: var(--background-color);
    color: var(--input-color);
    width: 100%;

    &::placeholder {
      color: var(--placeholder-color);
    }

    &::-webkit-search-cancel-button,
    &::-webkit-search-decoration {
      appearance: none;
    }

    &[data-focused] {
      outline: 2px solid var(--outline-color);

      --border-color: transparent;
      --outline-color: #{color(violet-600)};
    }

    &[data-invalid] {
      --border-color: #{color(lobster-800)};
      --outline-color: #{color(lobster-800)};
    }

    &[data-hovered] {
      --border-color: #{color(violet-600)};
      --outline-color: #{color(violet-600)};
    }
  }

  &:has(.startAddon) input {
    border-top-left-radius: 0;
    border-bottom-left-radius: 0;
  }

  &:has(.endAddon) input {
    border-top-right-radius: 0;
    border-bottom-right-radius: 0;
  }

  &[data-disabled] {
    --addon-border: #{border(grey-400)};
    --addon-color: #{color(grey-600)};
    --addon-background: #{color(grey-100)};
    --background-color: #{color(grey-100)};
    --input-color: #{color(grey-600)};
    --border-color: #{color(grey-400)};
    --placeholder-color: #{color(grey-600)};
  }
}

.inputField[data-size='small'] {
  input,
  textarea {
    @include typography.caption;
    @include typography.medium;

    padding: 6px 12px;

    &.clearable {
      padding-right: 30px;
    }

    ::placeholder {
      @include typography.caption;
      @include typography.medium;
    }
  }

  &:has(.icon) {
    input,
    textarea {
      padding-left: spacing(5);
    }
  }

  &:has(.endIcon) {
    input,
    textarea {
      padding-right: spacing(5);
    }
  }

  &:has(.control) {
    input,
    textarea {
      padding-right: spacing(5);
    }
  }

  .icon,
  .endAddonIcon {
    @include icon-size(sm);

    left: 12px;
    top: 6px;
  }

  .media {
    width: 18px;
    height: 18px;
  }

  .endIcon {
    @include icon-size(sm);

    right: 12px;
    top: 6px;
  }

  .control {
    right: 12px;
    top: 4px;
    width: 24px;
    height: 24px;
    padding: 0;
    border-radius: 4px;
  }

  .addon {
    @include typography.caption;
    @include typography.medium;

    padding: 6px 12px;
  }
}

.inputField[data-size='medium'] {
  input,
  textarea {
    @include typography.body2;
    @include typography.regular;

    padding: 7px 12px;

    &.clearable {
      padding-right: 30px;
    }

    ::placeholder {
      @include typography.body2;
      @include typography.regular;
    }
  }

  &:has(.icon) {
    input,
    textarea {
      padding-left: spacing(5);
    }
  }

  &:has(.endIcon) {
    input,
    textarea {
      padding-right: spacing(5);
    }
  }

  &:has(.control) {
    input,
    textarea {
      padding-right: spacing(5);
    }
  }

  .icon,
  .endAddonIcon {
    @include icon-size(sm);

    left: 12px;
    top: 9px;
  }

  .media {
    width: 22px;
    height: 22px;
  }

  .endIcon {
    @include icon-size(sm);

    right: 12px;
    top: 9px;
  }

  .control {
    right: 16px;
    top: 7px;
    width: 24px;
    height: 24px;
    padding: 0;
    border-radius: 4px;
  }

  .addon {
    @include typography.body2;
    @include typography.medium;

    padding: 7px 16px;
  }
}

.inputField[data-size='large'] {
  input,
  textarea {
    @include typography.body1;
    @include typography.regular;

    padding: 11px 12px;

    &.clearable {
      padding-right: 30px;
    }

    ::placeholder {
      @include typography.body1;
      @include typography.regular;
    }
  }

  &:has(.icon) {
    input,
    textarea {
      padding-left: spacing(6);
    }
  }

  &:has(.endIcon) {
    input,
    textarea {
      padding-right: spacing(6);
    }
  }

  &:has(.control) {
    input,
    textarea {
      padding-right: 32px;
    }
  }

  .icon,
  .endAddonIcon {
    @include icon-size(md);

    left: 12px;
    top: 12px;
  }

  .media {
    width: 24px;
    height: 24px;
  }

  .control {
    right: 12px;
    top: 5px;
  }

  .addon {
    @include typography.body1;
    @include typography.regular;

    padding: 11px 16px;
  }
}
/* stylelint-enable no-descending-specificity */
